import React from 'react';
import StatusDisplay from "./Components/Edit/OverviewComponents/StatusDisplay";
import { find, isEmpty } from "lodash";
import { FirewallStatusType, LogDestinationType } from "../../interfaces/interfaces";

interface Tag {
    Key: string;
    Value: string;
}

export const renderStatusWithCommit = (
    statusValue: string | undefined,
    commitInfo: any,
    fallbackValue: string = "N/A"
): React.ReactNode => {
    switch (statusValue) {
        case "Failed":
            return (
                <StatusDisplay
                    status="failed"
                    text={statusValue}
                    tooltipText={commitInfo?.CommitMessages?.[0] || ""}
                />
            );
        case "Success":
            return (
                <StatusDisplay
                    status="success"
                    text={statusValue}
                />
            );
        default:
            return statusValue ?? fallbackValue;
    }
};

export const POLICY_MANAGER = {
    PANORAMA: 'Panorama',
    STRATA: 'Strata',
    RULESTACK: 'Rulestack',
} as const;

export type PolicyManagerType = typeof POLICY_MANAGER[keyof typeof POLICY_MANAGER];

export const FIREWALL_STATE_FILTERS: Record<FirewallStatusType, string> = {
    CREATING: 'Creating',
    UPDATING: 'Updating',
    DELETING: 'Deleting',
    CREATE_COMPLETE: 'Create Complete',
    UPDATE_COMPLETE: 'Update Complete',
    DELETE_COMPLETE: 'Delete Complete',
    CREATE_FAIL: 'Create Fail',
    UPDATE_FAIL: 'Update Fail',
    DELETE_FAIL: 'Delete Fail'
};

export const getPolicyManager = (record: Record<string, any>) => {
    if (record?.Firewall?.LinkId?.includes('Link-SCM')) {
        return `Strata Cloud Manager (${record?.Firewall?.LinkId})`
    } else if (record?.Firewall?.LinkId) {
        return `Panorama (${record?.Firewall?.LinkId})`
    } else if (record?.Firewall?.RuleStackName) {
        return `Local Rulestack (${record?.Firewall?.RuleStackName})`
    } else if (record?.Firewall?.GlobalRuleStackName) {
        return `Global Rulestack (${record?.Firewall?.GlobalRuleStackName})`
    } else {
        // Default to Local Rulestack when no policy manager is specified
        return 'Local Rulestack';
    }
};

export const getFirewallStateDisplay = (fwStatus: Record<string, any>) => {
    const state = fwStatus?.FirewallStatus;
    const failureReason = fwStatus?.FailureReason
    const lowerCaseState = state?.toLowerCase();
    const firewallFilterState = FIREWALL_STATE_FILTERS[state as FirewallStatusType] ?? state;
    if (lowerCaseState?.includes('complete')) {
        return (<StatusDisplay status="success" text={firewallFilterState} />);
    } else if (lowerCaseState?.includes('fail')) {
        return (<StatusDisplay status="failed" text={firewallFilterState} tooltipText={failureReason || ""} />);
    } else {
        return (<StatusDisplay status="pending" text={firewallFilterState} />);
    }
};

export const getEndpointStateDisplay = (state: string) => {
    if (!state) {
        return <></>;
    }
    const lowerCaseState = state?.toLowerCase();
    if (lowerCaseState?.includes('accepted')) {
        return (<StatusDisplay status="success" text={state} />);
    } else if (lowerCaseState?.includes('rejected')) {
        return (<StatusDisplay status="failed" text={state} />);
    } else {
        return (<StatusDisplay status="pending" text={state} />);
    }
};

export const getFirewallName = (record: Record<string, any>) => {
    if (isEmpty(record)) {
        return '-';
    }

    // Check if Tags are directly under record or under record.Firewall
    const tags = record?.Firewall?.Tags || record?.Tags;

    if (!tags) {
        return '-';
    }

    const result = find(tags, (tag: Tag) => tag.Key === 'FirewallName') as Tag | undefined;

    return result ? result.Value : '-';
};

export const getFirewallTags = (tags: Record<string, any>, firewallName: string) => {
    if (isEmpty(tags) && isEmpty(firewallName)) {
        return [];
    }
    const index = tags.findIndex(tag => tag.Key === "FirewallName");
    if (index !== -1) {
        tags[index] = { ...tags[index], Value: firewallName };
    }
    else {
        tags.push({Key: "FirewallName", Value: firewallName})
    }

    return tags;
};

export const prepareData = (records: Record<string, any>[]) => {
    if (isEmpty(records)) {
        return [];
    }
    return records.map((record) => {
        return { ...record, Firewall: { ...record.Firewall, 'PolicyManager': getPolicyManager(record), 'FirewallName': getFirewallName(record)} }
    })
};

export const determinePolicyManaged = (firewallData: Record<string, any>): string => {
    if (firewallData?.Firewall?.LinkId?.includes('Link-SCM')) {
        return POLICY_MANAGER.STRATA;
    } else if (firewallData?.Firewall?.LinkId) {
        return POLICY_MANAGER.PANORAMA;
    } else if (firewallData?.Firewall?.RuleStackName) {
        return POLICY_MANAGER.RULESTACK;
    } else {
        return POLICY_MANAGER.RULESTACK;
    }
};

const UPDATING_STATUSES: FirewallStatusType[] = ['CREATING', 'UPDATING', 'CREATE_FAIL', 'DELETING'];

export const isFirewallUpdating = (firewallStatus: FirewallStatusType): boolean => {
    return UPDATING_STATUSES.includes(firewallStatus);
};

export const formatChangeProtection = (changeProtection: string[]): string => {
    return changeProtection.map(item => {
        if (item === 'GlobalFirewallAdmin') {
            return 'Global Firewall Admin';
        } else if (item.includes('/LocalFirewallAdmin')) {
            const [accountId] = item.split('/');
            return `Local Firewall Admin (${accountId})`;
        } else {
            return item;
        }
    }).join(', ');
};

export const formatIPPoolType = (ipPoolType: string): string => {
    if (ipPoolType === 'AWSService') {
        return 'AWS Service IPs';
    }
    return ipPoolType; // For BYOIP and any other values, return as is
};

export const getLogsMetricsEndpoints = (logDestinationType?: LogDestinationType, logDestination?: string, type?: 'Logs' | 'Metrics', region?: 'string'): string => {
    //"S3" | "CloudWatchLogs" | "KinesisDataFirehose" | "metrics"
    let urlFormat = '';
    if(logDestinationType === 'CloudWatchLogs') {
        urlFormat = `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${logDestination}`;
    }
    if(logDestinationType === 'S3') {
        urlFormat = `https://s3.console.aws.amazon.com/s3/buckets/${logDestination}?region=${region}&tab=objects`;
    }
    if(logDestinationType === 'KinesisDataFirehose') {
        urlFormat = `https://${region}.console.aws.amazon.com/kinesis/home?region=${region}#/streams/details/${logDestination}/monitoring`;
    }
    if(type === 'Metrics' && logDestinationType !== undefined) {
        urlFormat = `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#metricsV2:graph=~()`;
    }
    return urlFormat;
}
